import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MOBILE_BREAKPOINT = '(max-width: 768px)';

const getMenuStyle = (triggerEl) => {
  const rect = triggerEl.getBoundingClientRect();
  return {
    position: 'fixed',
    top: rect.bottom + 4,
    right: window.innerWidth - rect.right,
    zIndex: 1100,
  };
};

const BurgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const HeaderActionsMenu = ({ items, label, ariaLabel = 'Actions' }) => {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_BREAKPOINT).matches);

  const onToggle = () => setIsOpen((open) => !open);
  const onClose = () => setIsOpen(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
    const handleChange = (event) => {
      setIsMobile(event.matches);
      setIsOpen(false);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return;
    }

    if (triggerRef.current) {
      setMenuStyle(getMenuStyle(triggerRef.current));
    }
  }, [isOpen, items, isMobile]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        containerRef.current?.contains(target)
        || dropdownRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleReposition = () => {
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen]);

  if (!items || items.length === 0) {
    return null;
  }

  const dropdown = isOpen && menuStyle
    ? createPortal(
      <div
        ref={dropdownRef}
        className="table-actions-menu-dropdown"
        style={menuStyle}
        role="menu"
      >
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            className={`table-actions-menu-item${item.danger ? ' danger' : ''}`}
            onClick={() => {
              item.onClick();
              onClose();
            }}
          >
            {item.icon && <span className="table-actions-menu-item-icon">{item.icon}</span>}
            <span>{item.label}</span>
          </button>
        ))}
      </div>,
      document.body
    )
    : null;

  return (
    <div className="header-actions-menu" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={
          isMobile
            ? 'header-actions-menu-trigger header-actions-menu-trigger-mobile'
            : 'salon-button secondary header-actions-menu-trigger header-actions-menu-trigger-desktop'
        }
        onClick={onToggle}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {isMobile ? (
          <BurgerIcon />
        ) : (
          <>
            {label}
            <svg className="header-actions-menu-chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </>
        )}
      </button>
      {dropdown}
    </div>
  );
};

export default HeaderActionsMenu;
